/**
 * Read-only data preview for a Dataset (ADR-0026) — the uniform query seam in
 * action. Renders the first page of rows via `POST /datasets/{id}/preview`,
 * with column headers from `/fields`. Internal and external look identical —
 * that's the point of L1.
 */
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Alert } from "@/components/ui/alert";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import { datasetsApi, type Dataset } from "@/lib/api-datasets";

export interface DatasetPreviewDialogProps {
  dataset: Dataset | null;
  onOpenChange: (open: boolean) => void;
}

export function DatasetPreviewDialog({ dataset, onOpenChange }: DatasetPreviewDialogProps) {
  const { t } = useTranslation();
  const open = dataset !== null;

  const fieldsQ = useQuery({
    queryKey: ["datasets", dataset?.id, "fields"],
    queryFn: () => datasetsApi.fields(dataset!.id),
    enabled: open,
  });
  const previewQ = useQuery({
    queryKey: ["datasets", dataset?.id, "preview"],
    queryFn: () => datasetsApi.preview(dataset!.id, { page: 1, page_size: 25 }),
    enabled: open,
  });

  const cols = (fieldsQ.data ?? []).map((f) => f.name);
  const loading = fieldsQ.isLoading || previewQ.isLoading;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="text-sm font-semibold">
            {t("datasets.preview.title", "Anteprima")} · {dataset?.name}
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          {loading ? (
            <div className="flex items-center gap-2 py-8 text-xs text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
              {t("datasets.preview.loading", "Esecuzione query…")}
            </div>
          ) : previewQ.isError ? (
            <Alert tone="danger" title={t("datasets.preview.failed", "Query fallita")}>
              {(previewQ.error as Error).message}
            </Alert>
          ) : (
            <>
              <div className="mb-2 text-xs text-muted-foreground">
                {t("datasets.preview.total", "{{n}} righe totali", { n: previewQ.data?.total ?? 0 })}
              </div>
              <div className="max-h-[60vh] overflow-auto rounded-lg border border-border/60">
                <Table>
                  <Thead>
                    <Tr>
                      {cols.map((c) => (
                        <Th key={c} className="whitespace-nowrap font-mono text-[11px]">{c}</Th>
                      ))}
                    </Tr>
                  </Thead>
                  <Tbody>
                    {(previewQ.data?.rows ?? []).map((row, i) => (
                      <Tr key={i}>
                        {cols.map((c) => (
                          <Td key={c} className="max-w-[16rem] truncate text-xs">
                            {_cell(row[c])}
                          </Td>
                        ))}
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
                {(previewQ.data?.rows ?? []).length === 0 && (
                  <p className="px-3 py-6 text-center text-xs text-muted-foreground">
                    {t("datasets.preview.empty", "Nessuna riga.")}
                  </p>
                )}
              </div>
            </>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

function _cell(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}
